import { Alert, Button, Skeleton, Typography } from "@mui/material";
import { MarkdownText } from "src/components/ui/MarkdownText";
import { useGetDevices } from "src/hooks/queries";
import { useDownloadQualityEventFile } from "src/hooks/queries/quality-event-mutations";
import { BaseUser } from "src/stores/models/user";
import {
  isQualityEventDocumentReference,
  QualityEventField,
  QualityEventValue,
} from "src/types/quality-events";
import { getUserDisplayName } from "src/utils/user";

type QualityEventFileValueProps = {
  files: { id: string; filename: string }[];
  orgId: string;
  value: string[];
};

const QualityEventFileValue = ({
  files,
  orgId,
  value,
}: QualityEventFileValueProps) => {
  const download = useDownloadQualityEventFile({ orgId });
  const attachments = value.map((id, index) => {
    const filename = files.find((file) => file.id === id)?.filename;
    return {
      id,
      downloadFilename: filename ?? `attachment-${index + 1}`,
      label: filename ?? `Attachment ${index + 1}`,
    };
  });

  return (
    <div className="flex flex-col items-start gap-2">
      {attachments.map(({ id, downloadFilename, label }) => (
        <Button
          key={id}
          size="small"
          className="text-base"
          onClick={() =>
            download.mutate({
              fileId: id,
              filename: downloadFilename,
            })
          }
        >
          {label}
        </Button>
      ))}
      {download.isError && (
        <Alert severity="error">Unable to download this attachment.</Alert>
      )}
    </div>
  );
};

const QualityEventDeviceValue = ({
  orgId,
  value,
}: {
  orgId: string;
  value: QualityEventValue;
}) => {
  const devices = useGetDevices({ orgId });
  if (devices.isLoading) return <Skeleton width={160} />;
  const device = devices.data?.find(({ id }) => id === value);
  const displayValue = device?.name ?? String(value);
  return <Typography variant="body1">{displayValue}</Typography>;
};

export const QualityEventFieldValue = ({
  field,
  value,
  files,
  orgId,
  users,
}: {
  field: QualityEventField;
  value: QualityEventValue | undefined;
  files: { id: string; filename: string }[];
  orgId: string;
  users: BaseUser[];
}) => {
  if (
    value == null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  )
    return <Typography variant="body1">Not provided</Typography>;

  switch (field.type) {
    case "device":
      return <QualityEventDeviceValue orgId={orgId} value={value} />;
    case "document_reference":
      if (isQualityEventDocumentReference(value))
        return (
          <Typography variant="body1" className="whitespace-pre-wrap">
            {value.displaySnapshot}
          </Typography>
        );
      break;
    case "rich_text":
      if (typeof value === "string")
        return <MarkdownText>{value}</MarkdownText>;
      break;
    case "file":
      if (Array.isArray(value))
        return (
          <QualityEventFileValue files={files} orgId={orgId} value={value} />
        );
      break;
    case "user": {
      const user = users.find((candidate) => candidate.id === value);
      const displayValue = getUserDisplayName(user ?? {});
      return (
        <Typography variant="body1" className="whitespace-pre-wrap">
          {displayValue}
        </Typography>
      );
    }
  }

  const displayValue = (Array.isArray(value) ? value : [value])
    .map((item) =>
      typeof item === "boolean"
        ? item
          ? "Yes"
          : "No"
        : (field.options?.find((option) => option.value === item)?.label ??
          String(item))
    )
    .join(", ");

  return (
    <Typography variant="body1" className="whitespace-pre-wrap">
      {displayValue}
    </Typography>
  );
};
